import { PointerEvent, useCallback, useEffect, useRef, useState } from 'react';

type Position = {
  x: number;
  y: number;
};

const VIEWPORT_PADDING = 12;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const useDraggableEmergencyPosition = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragOffsetRef = useRef<Position>({ x: 0, y: 0 });
  const [position, setPosition] = useState<Position | null>(null);

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    const container = containerRef.current;

    if (!container || (event.target instanceof Element && event.target.closest('[data-emergency-action]'))) {
      return;
    }

    const rect = container.getBoundingClientRect();
    dragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    setPosition({
      x: rect.left,
      y: rect.top,
    });
    container.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
    const container = containerRef.current;

    if (!container || !container.hasPointerCapture(event.pointerId)) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const maxX = window.innerWidth - rect.width - VIEWPORT_PADDING;
    const maxY = window.innerHeight - rect.height - VIEWPORT_PADDING;

    setPosition({
      x: clamp(event.clientX - dragOffsetRef.current.x, VIEWPORT_PADDING, Math.max(VIEWPORT_PADDING, maxX)),
      y: clamp(event.clientY - dragOffsetRef.current.y, VIEWPORT_PADDING, Math.max(VIEWPORT_PADDING, maxY)),
    });
  }, []);

  const onPointerUp = useCallback((event: PointerEvent<HTMLElement>) => {
    const container = containerRef.current;

    if (container?.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
  }, []);

  useEffect(() => {
    if (!position) {
      return;
    }

    const handleResize = () => {
      const container = containerRef.current;

      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      setPosition(currentPosition => {
        if (!currentPosition) {
          return currentPosition;
        }

        return {
          x: clamp(
            currentPosition.x,
            VIEWPORT_PADDING,
            Math.max(VIEWPORT_PADDING, window.innerWidth - rect.width - VIEWPORT_PADDING),
          ),
          y: clamp(
            currentPosition.y,
            VIEWPORT_PADDING,
            Math.max(VIEWPORT_PADDING, window.innerHeight - rect.height - VIEWPORT_PADDING),
          ),
        };
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [position]);

  return {
    containerRef,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
    },
    position,
  };
};

export default useDraggableEmergencyPosition;
